import { useEffect, useState } from "react";
import { X, FileSpreadsheet, Upload, AlertTriangle, PlusCircle, Repeat } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Shared dry-run preview modal used by every bulk-upload page (Program
 * Bookings, Weekend Bookings, Arrivals). The moment the user picks a
 * file we open this modal and POST to `endpoint?dry_run=true` — the
 * backend parses the file, dedupes by name, and returns exactly which
 * existing DB rows would be replaced vs which are brand new. The
 * Founder can then Cancel or hit Confirm to commit.
 */
export default function UploadPreviewModal({ file, endpoint, label, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!file || !endpoint) return;
    const fd = new FormData(); fd.append("file", file);
    setLoading(true); setError("");
    api.post(`${endpoint}?dry_run=true`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => setPreview(data))
      .catch(err => setError(err?.response?.data?.detail || "Could not preview the file"))
      .finally(() => setLoading(false));
  }, [file, endpoint]);

  const confirm = async () => {
    if (!file || !endpoint) return;
    const fd = new FormData(); fd.append("file", file);
    setSubmitting(true);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const bits = [`Inserted ${data.inserted}`];
      if (data.replaced) bits.push(`replaced ${data.replaced}`);
      if (data.gender_synced) bits.push(`gender synced on ${data.gender_synced} weekend rows`);
      toast.success(bits.join(" · "));
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="upload-preview-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1.5"><FileSpreadsheet size={12}/> Upload preview</div>
            <h3 className="font-display text-lg font-semibold">Review before importing {label}</h3>
            <p className="text-xs text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1.5">
              <span className="truncate max-w-[380px]">{file?.name || "—"}</span>
              {file?.size ? <span>· {Math.max(1, Math.round(file.size / 1024))} KB</span> : null}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {loading && <div className="text-sm text-[color:var(--aii-muted)]" data-testid="upload-preview-loading">Parsing your sheet…</div>}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800" data-testid="upload-preview-error">
              <AlertTriangle size={14} className="mt-0.5"/> <span>{error}</span>
            </div>
          )}
          {preview && (
            <>
              <div className="grid sm:grid-cols-3 gap-2" data-testid="upload-preview-summary">
                <div className="aii-card p-3 border-l-4 border-sky-400">
                  <div className="text-[10px] uppercase tracking-widest text-sky-800 font-bold">In your file</div>
                  <div className="text-2xl font-display font-semibold text-sky-900" data-testid="preview-total">{preview.total_in_file}</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)]">after in-file dedupe</div>
                </div>
                <div className="aii-card p-3 border-l-4 border-emerald-500">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold flex items-center gap-1"><PlusCircle size={9}/> Brand new</div>
                  <div className="text-2xl font-display font-semibold text-emerald-900" data-testid="preview-new">{preview.will_add_new}</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)]">will be inserted</div>
                </div>
                <div className="aii-card p-3 border-l-4 border-amber-500">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold flex items-center gap-1"><Repeat size={9}/> Replacing</div>
                  <div className="text-2xl font-display font-semibold text-amber-900" data-testid="preview-replace">{preview.will_replace}</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)]">existing records overwritten</div>
                </div>
              </div>

              {preview.will_replace > 0 && (
                <section data-testid="preview-replace-list">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-1.5 flex items-center gap-1">
                    <Repeat size={11}/> These existing records will be overwritten
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.replace_list.map((n, i) => (
                      <span key={`r-${i}`} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 font-medium">{n}</span>
                    ))}
                  </div>
                </section>
              )}

              {preview.will_add_new > 0 && (
                <section data-testid="preview-new-list">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold mb-1.5 flex items-center gap-1">
                    <PlusCircle size={11}/> Brand new — inserted as fresh rows
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.new_list.map((n, i) => (
                      <span key={`n-${i}`} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">{n}</span>
                    ))}
                  </div>
                </section>
              )}

              {preview.total_in_file === 0 && (
                <div className="text-sm text-[color:var(--aii-muted)] italic bg-slate-50 p-4 rounded border border-slate-200">
                  No rows found in this file — check the column headers match the template.
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50" data-testid="upload-preview-cancel">Cancel</button>
          <button data-testid="upload-preview-confirm" onClick={confirm}
                  disabled={loading || submitting || !!error || (preview && preview.total_in_file === 0)}
                  className="text-sm px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
            <Upload size={13}/> {submitting ? "Importing…" : preview ? `Confirm — import ${preview.total_in_file}` : "Confirm import"}
          </button>
        </div>
      </div>
    </div>
  );
}
